import * as Popover from "@radix-ui/react-popover";
import { useState } from "react";
import { Check, ListTodo } from "lucide-react";
import { cn } from "@/lib/cn";
import { useTasks } from "@/features/data";
import type { Task } from "@/api/types";

interface TaskPickerProps {
  projectId: string | null;
  value: string | null;
  onChange: (id: string | null, task: Task | null) => void;
  compact?: boolean;
}

/** TaskPicker renders nothing until the selected project actually has tasks. */
export function TaskPicker({ projectId, value, onChange, compact }: TaskPickerProps) {
  const { data: tasks = [] } = useTasks(projectId);
  const [open, setOpen] = useState(false);

  const active = tasks.filter((t) => !t.archived);
  const selected = tasks.find((t) => t.id === value) ?? null;
  if (!projectId || active.length === 0) return null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 rounded-md border border-line px-2.5 text-sm transition-colors hover:border-line-strong",
            compact ? "h-8" : "h-9",
            selected ? "text-ink" : "text-faint",
          )}
        >
          <ListTodo className="size-4" />
          <span className="max-w-32 truncate">{selected ? selected.name : "Task"}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className="z-50 w-64 overflow-hidden rounded-lg border border-line bg-elevated shadow-xl">
          <div className="max-h-64 overflow-auto py-1">
            <Row
              active={value === null}
              onClick={() => {
                onChange(null, null);
                setOpen(false);
              }}
            >
              <span className="text-faint">No task</span>
            </Row>
            {active.map((t) => (
              <Row
                key={t.id}
                active={value === t.id}
                onClick={() => {
                  onChange(t.id, t);
                  setOpen(false);
                }}
              >
                <span className="truncate text-ink">{t.name}</span>
              </Row>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Row({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface">
      {children}
      {active && <Check className="ml-auto size-4 text-accent" />}
    </button>
  );
}
